/* =====================================================
   bs-quick-add.js
   ----------------------------------------------------
   Tap "+" on a product card → if single variant, add to
   bag and open cart drawer. If multi-variant, open a
   small popover (or mobile bottom-sheet) with chips.
   Tap a chip → adds that variant to bag, drawer opens.

   Reads variant data from `data-bs-qa-variants` JSON
   on the trigger button so we don't need a roundtrip.

   Cart drawer integration mirrors the cross-sell flow
   in cart-drawer-d.js: POST /cart/add.js → fire
   cart:build → click .js-drawer-open-cart to open.
   ===================================================== */
(function () {
  'use strict';

  var BTN_SEL  = '.bs-qa__plus';
  var POP_SEL  = '.bs-qa-pop';
  var LAYER_ID = 'bs-qa-layer';
  var BACKDROP_ID = 'bs-qa-backdrop';

  var openState = null; // { btn, pop }

  function ensureLayer() {
    var layer = document.getElementById(LAYER_ID);
    if (!layer) {
      layer = document.createElement('div');
      layer.id = LAYER_ID;
      document.body.appendChild(layer);
    }
    var bd = document.getElementById(BACKDROP_ID);
    if (!bd) {
      bd = document.createElement('div');
      bd.id = BACKDROP_ID;
      bd.className = 'bs-qa-backdrop';
      document.body.appendChild(bd);
      bd.addEventListener('click', closePop);
    }
    return layer;
  }

  function readVariants(btn) {
    var raw = btn.getAttribute('data-bs-qa-variants');
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[bs-quick-add] bad variant JSON', e);
      return [];
    }
  }

  function buildPop(btn, variants) {
    var optionLabel = btn.getAttribute('data-bs-qa-option-label') || 'Pick an option';
    var pdpUrl = btn.getAttribute('data-bs-qa-url') || '#';
    var layer = ensureLayer();

    // Re-use existing pop for this button if present
    var existing = layer.querySelector('[data-bs-qa-for="' + btn.id + '"]');
    if (existing) return existing;

    var pop = document.createElement('div');
    pop.className = 'bs-qa-pop';
    pop.setAttribute('data-bs-qa-for', btn.id);

    var html = '<p class="bs-qa-pop__label">' + escape(optionLabel) + '</p>';
    html += '<div class="bs-qa-pop__chips">';
    variants.forEach(function (v) {
      var disabled = v.available === false ? ' disabled aria-disabled="true"' : '';
      html += '<button type="button" class="bs-qa-pop__chip" data-variant-id="' + v.id + '"' + disabled + '>' + escape(v.label) + '</button>';
    });
    html += '</div>';
    html += '<a href="' + pdpUrl + '" class="bs-qa-pop__pdp-link">View product →</a>';
    pop.innerHTML = html;
    layer.appendChild(pop);
    return pop;
  }

  function escape(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  function positionPop(pop, btn) {
    if (window.innerWidth <= 768) return; // mobile = bottom sheet via CSS
    var r = btn.getBoundingClientRect();
    var popWidth = 220;
    var margin = 12;
    var left = r.right - popWidth;
    if (left < margin) left = margin;
    if (left + popWidth > window.innerWidth - margin) {
      left = window.innerWidth - margin - popWidth;
    }
    pop.style.left = left + 'px';
    pop.style.bottom = (window.innerHeight - r.top + 8) + 'px';
    pop.style.top = 'auto';
    pop.style.right = 'auto';
    var arrowX = r.left + r.width / 2 - left - 5;
    pop.style.setProperty('--bs-qa-arrow-x', arrowX + 'px');
  }

  function openPop(btn) {
    var variants = readVariants(btn);
    if (!variants.length) return;
    closePop();
    var pop = buildPop(btn, variants);
    positionPop(pop, btn);
    // Force reflow before adding is-open so transition runs
    void pop.offsetWidth;
    pop.classList.add('is-open');
    btn.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    var bd = document.getElementById(BACKDROP_ID);
    if (bd) bd.classList.add('is-open');
    openState = { btn: btn, pop: pop };
  }

  function closePop() {
    if (!openState) return;
    openState.pop.classList.remove('is-open');
    openState.btn.classList.remove('is-open');
    openState.btn.setAttribute('aria-expanded', 'false');
    var bd = document.getElementById(BACKDROP_ID);
    if (bd) bd.classList.remove('is-open');
    openState = null;
  }

  function openCartDrawer() {
    // Impulse exposes 'cart:open' as the official open hook (theme.js).
    // This is more reliable than synthesizing a click on .js-drawer-open-cart
    // because it skips any href navigation entirely.
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  function addVariant(btn, variantId, onDone) {
    if (!variantId) return;
    if (btn.getAttribute('aria-busy') === 'true') return; // re-entry guard
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-text');
    btn.textContent = '…';

    var resetButton = function (mark) {
      // Always recover to '+' regardless of starting state — guards against
      // stale '…' if a previous add stalled.
      if (!mark || mark === '+') {
        // Restore the CSS-drawn plus.
        btn.classList.remove('is-text');
        btn.textContent = '';
      } else {
        btn.classList.add('is-text');
        btn.textContent = mark;
      }
      btn.setAttribute('aria-busy', 'false');
    };

    // Hard timeout — if the network stalls (page navigation, slow store, etc.)
    // we still un-stick the button after 6s.
    var timedOut = false;
    var timeoutId = setTimeout(function () {
      timedOut = true;
      resetButton('+');
    }, 6000);

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        clearTimeout(timeoutId);
        if (timedOut) return;
        // Tell Impulse to rebuild internal cart state, then open drawer.
        document.dispatchEvent(new CustomEvent('cart:build'));
        btn.classList.add('is-text');
        btn.textContent = '✓';
        setTimeout(function () {
          resetButton('+');
          openCartDrawer();
          if (typeof onDone === 'function') onDone();
        }, 280);
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (timedOut) return;
        console.error('[bs-quick-add] add failed', err);
        resetButton('+');
      });
  }

  // ---------- Delegated click handler ----------
  // Bound in CAPTURE phase so we beat any document-level handlers Impulse
  // may have wired up (e.g. variant pickers, ajax-cart). Use
  // stopImmediatePropagation to also kill peer listeners on the same node.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest(BTN_SEL);
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var single = btn.getAttribute('data-bs-qa-single-variant');
      if (single) {
        addVariant(btn, single);
        return;
      }
      // multi-variant → toggle popover
      if (openState && openState.btn === btn) {
        closePop();
      } else {
        openPop(btn);
      }
      return;
    }

    var chip = e.target.closest('.bs-qa-pop__chip');
    if (chip && !chip.disabled) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var variantId = chip.getAttribute('data-variant-id');
      var triggerBtn = openState ? openState.btn : null;
      chip.classList.add('is-flash');
      setTimeout(function () {
        if (triggerBtn) {
          addVariant(triggerBtn, variantId, closePop);
        }
      }, 180);
      return;
    }

    var pdpLink = e.target.closest('.bs-qa-pop__pdp-link');
    if (pdpLink) {
      // Let it navigate naturally, but close the pop first.
      closePop();
      return;
    }

    // Click outside any popover → close
    if (openState) {
      if (!e.target.closest(POP_SEL) && !e.target.closest(BTN_SEL)) {
        closePop();
      }
    }
  }, true); // capture so we beat any card-level <a> handlers

  // Esc + scroll/resize close
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePop();
  });
  window.addEventListener('resize', closePop);
  window.addEventListener('scroll', function () {
    if (openState && window.innerWidth > 768) closePop();
  }, { passive: true });

})();
